import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react';

import { LbDatePicker, LbInput, LbSelect, HeightInput } from '@infoods/lifebase-ui';

import classes from './PersonalInfoStep.module.scss';
import {
  HiddenInput,
  InputBox,
  PhoneNumberInput,
  ReadOnlyInput,
} from '../../../../../../shared/components/form-components';
import {
  validateAge,
  validateEmail,
} from '../../../../../../shared/utils/reactHookFormUtils';
import { genderOptions } from '../../../../../../shared/constants/user-info/gender';
import { SubmitButton } from '../../../components/SubmitButton/SubmitButton';
import { TermsAcceptCheckbox } from '../../../../../../shared/components/TermsAcceptCheckbox/TermsAcceptCheckbox';
import { scanIqStore } from '../../../../../stores/scanIqStore';
import { entitySignUpStore } from '../../../../../../shared/stores/entity/entitySignUpStore';
import { ScanIqCustomer } from '../../../../../interfaces/ScanIqCustomer';
import { scanIqCustomerService } from '../../../../../services/scanIqCustomerService';
import { getFilteredTruthyFieldsObj } from '../../../../../../shared/utils/sortingAndGroupingUtil';

type PersonalInfoFormProps = {
  onCustomerCreated: () => void;
};
/*
  after 2fa step backend create customer with phone or email (based on verification channel)
  we should omit change phone or email if it is already set
*/
const PersonalInfoStep = observer(({ onCustomerCreated }: PersonalInfoFormProps) => {
  const { customer: initialCustomer } = scanIqStore;
  const { entityId, entityName } = entitySignUpStore;

  const { control, register, handleSubmit } = useForm<ScanIqCustomer>({
    defaultValues: getFilteredTruthyFieldsObj(initialCustomer || {}),
  });

  const [isCommunicationsAccepted, setIsCommunicationsAccepted] = useState(false);
  const [isMarketingAccepted, setIsMarketingAccepted] = useState(false);

  if (!entityId) {
    throw new Error('Entity ID is not defined');
  }

  const generalInputProps = {
    control,
    size: 'small',
    variant: 'standard',
  } as const;

  const onSubmit = handleSubmit(async (data) => {
    // Note: We should always update the customer on this step because 2FA step always creates customer record on backend when customer does not exist
    await scanIqCustomerService.updateCustomer(data);

    onCustomerCreated();
  });

  return (
    <form onSubmit={onSubmit} className={classes.container}>
      <InputBox title="Personal Data">
        <HiddenInput register={register} name="entityId" value={entityId} />
        <LbInput
          {...generalInputProps}
          name="firstName"
          label="First Name"
          rules={{ required: 'First Name is required' }}
        />
        <LbInput
          {...generalInputProps}
          name="lastName"
          label="Last Name"
          rules={{ required: 'Last Name is required' }}
        />
        {initialCustomer?.email ? (
          <ReadOnlyInput {...generalInputProps} name="email" label="Email address" />
        ) : (
          <LbInput
            {...generalInputProps}
            name="email"
            label="Email address"
            type="email"
            rules={{ required: 'Email is required', validate: { validateEmail } }}
          />
        )}
        {initialCustomer?.phone ? (
          <ReadOnlyInput {...generalInputProps} name="phone" label="Cell Phone" />
        ) : (
          <PhoneNumberInput
            {...generalInputProps}
            name="phone"
            label="Cell Phone"
            rules={{ required: 'Cell Phone is required' }}
          />
        )}
        <LbDatePicker
          {...generalInputProps}
          name="dateOfBirth"
          label="Date of Birth"
          helperText="You need to be at least 14 years to join the LifeBase"
          openTo="year"
          rules={{
            required: 'Date of Birth is required',
            validate: { validateMinAge: (value) => validateAge(value, 14) },
          }}
        />
        <LbSelect
          {...generalInputProps}
          name="gender"
          label="Gender"
          options={genderOptions}
          rules={{ required: 'Gender is required' }}
        />
        <HeightInput
          {...generalInputProps}
          name="height"
          label="Height"
          rules={{ required: 'Height is required' }}
        />
      </InputBox>
      <div>
        <TermsAcceptCheckbox
          isCommunicationsAccepted={isCommunicationsAccepted}
          isMarketingAccepted={isMarketingAccepted}
          setIsCommunicationsAccepted={setIsCommunicationsAccepted}
          setIsMarketingAccepted={setIsMarketingAccepted}
          entityName={entityName}
        />
        <SubmitButton />
      </div>
    </form>
  );
});

export { PersonalInfoStep };
